/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {NumberString, ResponseData} from '@dvrd/dvr-controls';
import React, {useContext, useRef, useState} from 'react';
import {AppContext} from '../misc/appContext';
import ConnectionComplete from './connectionComplete';
import {planHistoryImport} from '../../util/requestSender';
import {Navigate, useNavigate} from 'react-router';
import {createErrorMessage} from '../../util/utils';
import {AutoUploadType, InvoiceSettingsErrors} from '../../util/interfaces';
import Account from '../../models/accountModel';
import {NotificationType} from '../../util/enums';
import {DASHBOARD_ROUTE} from '../../util/constants';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const EMPTY_ERRORS: InvoiceSettingsErrors = {
    invoicePrefix: null,
    invoiceStart: null,
    oldInvoicePrefix: null,
    oldInvoiceStart: null
};

export enum InvoiceNumberType {
    STANDARD,
    CUSTOM
}

export enum ConnectionSettingsStep {
    INTRO,
    IMPORT_HISTORY,
    INVOICE_NUMBER,
    COMPLETE,
    COMPLETE_IMPORT,
    AUTO_UPLOAD,
    AUTO_UPLOAD_OLD,
    AUTO_UPLOAD_OLD_NUMBER,
    NOTIFICATIONS
}

export default function ConnectionCompleteController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [importHistory, setImportHistory] = useState<boolean | null>(null);
    const [autoUpload, setAutoUpload] = useState<AutoUploadType | null>(null);
    const [autoUploadOld, setAutoUploadOld] = useState<boolean | null>(null);
    const [oldInvoiceNumPrefix, setOldInvoiceNumPrefix] = useState('{jaar}pre-');
    const [oldInvoiceNumStart, setOldInvoiceNumStart] = useState('0001');
    const [errors, setErrors] = useState(EMPTY_ERRORS);
    const [invoiceNumberType, setInvoiceNumberType] = useState<InvoiceNumberType | null>(null);
    const [invoicePreview, setInvoicePreview] = useState('0001');
    const [invoicePrefix, setInvoicePrefix] = useState(account?.settings.invoicePrefix ?? '{jaar}-');
    const [invoiceStart, setInvoiceStart] = useState(invoicePreview);
    const [enableNotifications, setEnableNotifications] = useState<Set<NotificationType>>(
        new Set([
            NotificationType.ORDER,
            NotificationType.STOCK,
            NotificationType.REVIEWS,
            NotificationType.BUY_BOX,
            NotificationType.PRODUCT_HIJACK
        ])
    );
    const [step, setStep] = useState(ConnectionSettingsStep.INTRO);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const previousSteps = useRef<Array<ConnectionSettingsStep>>([]);

    if (!account) return <Navigate to={DASHBOARD_ROUTE} />;

    function onToggleNotification(notificationType: NotificationType) {
        return function () {
            const enabled = new Set(enableNotifications);
            if (enabled.has(notificationType)) enabled.delete(notificationType);
            else enabled.add(notificationType);
            setEnableNotifications(enabled);
        };
    }

    function onChangeAutoUploadOld(value: boolean | null) {
        return function () {
            setAutoUploadOld(value ?? false);
            if (value === null) window.setTimeout(onClickNext);
        };
    }

    function onChangeOldPrefix(value: string) {
        setOldInvoiceNumPrefix(value);
    }

    function onChangeOldStart(value: NumberString) {
        setOldInvoiceNumStart(value.stringValue);
        setInvoicePreview(value.stringValue);
    }

    function onSelectInvoiceNumberType(numType: InvoiceNumberType) {
        return function () {
            setInvoiceNumberType(numType);
        };
    }

    function onChooseAutoUpload(value: AutoUploadType, isSkip?: boolean) {
        return function () {
            setAutoUpload(value);
            if (isSkip) onClickNext();
        };
    }

    function onClickBack() {
        const previousStep = getPreviousStep();
        if (previousStep) return setStep(previousStep);
        if (step === ConnectionSettingsStep.COMPLETE) setStep(ConnectionSettingsStep.IMPORT_HISTORY);
        else if (step === ConnectionSettingsStep.COMPLETE_IMPORT) setStep(ConnectionSettingsStep.INVOICE_NUMBER);
        else if (step === ConnectionSettingsStep.INVOICE_NUMBER) setStep(ConnectionSettingsStep.IMPORT_HISTORY);
        else if (step === ConnectionSettingsStep.IMPORT_HISTORY) setStep(ConnectionSettingsStep.INTRO);
    }

    function onClickNext() {
        previousSteps.current = previousSteps.current.concat(step);
        if (step === ConnectionSettingsStep.INTRO) setStep(ConnectionSettingsStep.IMPORT_HISTORY);
        else if (step === ConnectionSettingsStep.IMPORT_HISTORY)
            setStep(importHistory ? ConnectionSettingsStep.INVOICE_NUMBER : ConnectionSettingsStep.AUTO_UPLOAD);
        else if (step === ConnectionSettingsStep.INVOICE_NUMBER && validateInvoiceNumberValues())
            setStep(ConnectionSettingsStep.AUTO_UPLOAD);
        else if (step === ConnectionSettingsStep.COMPLETE || step === ConnectionSettingsStep.COMPLETE_IMPORT)
            saveData();
        else if (step === ConnectionSettingsStep.AUTO_UPLOAD) {
            if (autoUpload === AutoUploadType.NONE) setStep(ConnectionSettingsStep.NOTIFICATIONS);
            else setStep(ConnectionSettingsStep.AUTO_UPLOAD_OLD);
        } else if (step === ConnectionSettingsStep.AUTO_UPLOAD_OLD) {
            if (autoUploadOld) setStep(ConnectionSettingsStep.AUTO_UPLOAD_OLD_NUMBER);
            else setStep(ConnectionSettingsStep.NOTIFICATIONS);
        } else if (step === ConnectionSettingsStep.AUTO_UPLOAD_OLD_NUMBER) {
            if (validateOldInvoiceNumber()) setStep(ConnectionSettingsStep.NOTIFICATIONS);
        } else if (step === ConnectionSettingsStep.NOTIFICATIONS)
            setStep(importHistory ? ConnectionSettingsStep.COMPLETE_IMPORT : ConnectionSettingsStep.COMPLETE);
    }

    function onChangeImportHistory(importHistory: boolean) {
        return function () {
            setImportHistory(importHistory);
        };
    }

    function onChangeSetting(key: string) {
        return function (value: string) {
            if (key === 'invoice_start') {
                setInvoiceStart(value);
                setInvoicePreview(value);
            } else setInvoicePrefix(value);
        };
    }

    function onChangeInvoiceNum(value: string) {
        setInvoicePreview(value);
    }

    function getPreviousStep(): ConnectionSettingsStep | undefined {
        const steps = previousSteps.current.slice();
        const step = steps.pop();
        previousSteps.current = steps;
        return step;
    }

    function validateInvoiceNumberValues(): boolean {
        let valid = true;
        const errs = Object.assign({}, errors);
        if (!invoicePrefix.length) {
            errs.invoicePrefix = t('connectionComplete.errors.invalid', 'Niet geldig');
            valid = false;
        }
        if (!invoiceStart.length) {
            errs.invoiceStart = t('connectionComplete.errors.invalid', 'Niet geldig');
            valid = false;
        }
        if (!valid) setErrors(errs);
        return valid;
    }

    function validateOldInvoiceNumber() {
        let valid = true;
        const errs = Object.assign({}, errors);
        if (!oldInvoiceNumPrefix.length) {
            errs.oldInvoicePrefix = t('connectionComplete.errors.invalid', 'Niet geldig');
            valid = false;
        }
        if (!oldInvoiceNumStart.length) {
            errs.oldInvoiceStart = t('connectionComplete.errors.invalid', 'Niet geldig');
            valid = false;
        }
        if (!valid) setErrors(errs);
        return valid;
    }

    function onResetError(key: keyof InvoiceSettingsErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function saveData() {
        const {settings} = account!;
        if (!settings || importHistory === null) return;
        setLoading(true);
        const useStandardInvoiceNumber = invoiceNumberType === InvoiceNumberType.STANDARD;
        const numPrefix = useStandardInvoiceNumber ? settings.invoicePrefix : invoicePrefix;
        const numStart = useStandardInvoiceNumber ? settings.invoiceStart : invoiceStart;
        planHistoryImport({
            data: {
                import_orders: importHistory,
                auto_upload: autoUpload ?? AutoUploadType.NONE,
                auto_upload_old: autoUploadOld ?? false,
                old_invoice_num_prefix: oldInvoiceNumPrefix,
                old_invoice_num_start: oldInvoiceNumStart,
                account_id: account!.id,
                invoice_num_prefix: numPrefix,
                invoice_num_start: numStart,
                is_default_num: useStandardInvoiceNumber,
                enable_notifications: Array.from(enableNotifications)
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    context.onChangeContext({account: new Account(data.account)});
                    navigate('/facturen');
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'connectionComplete.errors.startImportFailed',
                                    'Het starten van de import is niet gelukt.'
                                )
                        ),
                        t('connectionComplete.errors.importFailedTitle', 'Importeren mislukt')
                    );
            }
        });
    }

    return (
        <ConnectionComplete
            onClickNext={onClickNext}
            onSelectInvoiceNumberType={onSelectInvoiceNumberType}
            onClickBack={onClickBack}
            onChangeInvoiceNum={onChangeInvoiceNum}
            onChangeSetting={onChangeSetting}
            onChangeImportHistory={onChangeImportHistory}
            onResetError={onResetError}
            onChooseAutoUpload={onChooseAutoUpload}
            oldInvoiceNumStart={oldInvoiceNumStart}
            onChangeAutoUploadOld={onChangeAutoUploadOld}
            onChangeOldInvoiceNumPrefix={onChangeOldPrefix}
            onChangeOldInvoiceNumStart={onChangeOldStart}
            onToggleNotification={onToggleNotification}
            step={step}
            loading={loading}
            autoUploadOld={autoUploadOld}
            oldInvoiceNumPrefix={oldInvoiceNumPrefix}
            autoUpload={autoUpload}
            invoicePreview={invoicePreview}
            invoiceStart={invoiceStart}
            invoicePrefix={invoicePrefix}
            importHistory={importHistory}
            errors={errors}
            invoiceNumberType={invoiceNumberType}
            account={account}
            enableNotifications={enableNotifications}
        />
    );
}
